<template>
  <div>
    <Head title="Cobranzas" />

    <!-- Header & Actions -->
    <div class="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
      <h1 class="text-3xl font-bold text-gray-800">Gestión de Cobranzas</h1>
      
      <div class="flex flex-wrap items-center gap-3">
        <!-- Portal público del cliente -->
        <a
          v-if="account?.identification"
          :href="`/public/client?account_identification=${account.identification}`"
          target="_blank"
          class="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg flex items-center gap-2 transition-colors font-medium shadow-sm"
          title="Ver portal público de estado de cuenta"
        >
          <i class="fa fa-globe" />
          Portal del Cliente
        </a>
        <Link
          href="/cobranzas/avisos"
          class="px-4 py-2 bg-white border border-orange-300 hover:bg-orange-50 text-orange-700 rounded-lg flex items-center gap-2 transition-colors font-medium shadow-sm"
        >
          <i class="fa fa-list-check" />
          Ver Avisos
        </Link>
        <Link
          href="/cobranzas/aviso/create"
          class="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg flex items-center gap-2 transition-colors font-medium shadow-sm"
        >
          <i class="fa fa-file-invoice" />
          Crear Aviso de Cobro
        </Link>
        <a
          target="_blank" href="/cobranzas/report/pdf"
          class="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors font-medium shadow-sm"
        >
          <i class="fa fa-file-pdf text-red-600" />
          Reporte PDF
        </a>
      </div>
    </div>

    <!-- Stats -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      <!-- Total por Cobrar -->
      <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 transition-transform hover:-translate-y-1">
        <div class="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center text-red-600 mb-4 font-bold">
          <i class="fa fa-wallet text-lg" />
        </div>
        <p class="text-slate-500 text-sm font-medium mb-1">Total por Cobrar</p>
        <h3 class="text-3xl font-bold text-slate-800">{{ formatCurrency(stats.total_debt) }}</h3>
      </div>

      <!-- Clientes en Deuda -->
      <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 transition-transform hover:-translate-y-1">
        <div class="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center text-orange-600 mb-4 font-bold">
          <i class="fa fa-users text-lg" />
        </div>
        <p class="text-slate-500 text-sm font-medium mb-1">Clientes en Deuda</p>
        <h3 class="text-3xl font-bold text-slate-800">{{ stats.clients_count }}</h3>
      </div>

      <!-- Buscador -->
      <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 transition-transform hover:-translate-y-1">
        <div class="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center text-teal-600 mb-4 font-bold">
          <i class="fa fa-search text-lg" />
        </div>
        <p class="text-slate-500 text-sm font-medium mb-1">Búsqueda rápida</p>
        <div class="relative mt-2">
          <input
            v-model="form.search"
            type="text"
            placeholder="Nombre o cédula..."
            class="block w-full pl-8 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-teal-500 focus:border-teal-500 text-sm"
          />
          <i class="fa fa-search absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
        </div>
      </div>
    </div>

    <!-- Desktop Table -->
    <div class="hidden md:block bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <table class="w-full text-left text-sm">
        <thead>
          <tr class="bg-gray-50/50 border-b border-gray-100">
            <th class="py-4 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider">Cliente</th>
            <th class="py-4 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider">Contacto</th>
            <th class="py-4 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider text-center">Vencimiento</th>
            <th class="py-4 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider">Último Pago</th>
            <th class="py-4 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">Saldo</th>
            <th class="py-4 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider text-center">Acciones</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-50">
          <tr v-for="client in clients.data" :key="client.id" class="hover:bg-red-50/20 transition-colors group">
            <td class="py-4 px-6">
              <div class="font-semibold text-gray-800 group-hover:text-red-600 transition-colors">{{ client.name }}</div>
              <div class="text-xs text-gray-400">{{ client.identification || '-' }}</div>
            </td>
            <td class="py-4 px-6 text-sm text-gray-500">
              <div v-if="client.phone"><i class="fa fa-phone mr-1 text-xs text-gray-300" />{{ client.phone }}</div>
              <div v-if="client.email" class="truncate max-w-xs"><i class="fa fa-envelope mr-1 text-xs text-gray-300" />{{ client.email }}</div>
            </td>
            <td class="py-4 px-6 text-center">
              <span
                :class="getDaysClass(client.days_overdue)"
                class="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold"
              >
                <i class="fa fa-circle text-[8px]" />
                {{ client.days_overdue }} días
              </span>
            </td>
            <td class="py-4 px-6 text-sm text-gray-500">
              <div v-if="client.last_payment_date">
                <div>{{ formatDate(client.last_payment_date) }}</div>
                <div class="text-green-600 font-medium text-xs">{{ formatCurrency(client.last_payment_amount) }}</div>
              </div>
              <span v-else class="text-gray-300 italic text-xs">Sin pagos</span>
            </td>
            <td class="py-4 px-6 text-right font-bold text-red-600 text-base">
              {{ formatCurrency(client.balance) }}
            </td>
            <td class="py-4 px-6 text-center">
              <div class="flex items-center justify-center gap-1">
                <!-- Ver ficha de cliente -->
                <Link
                  :href="`/clients/${client.id}`"
                  class="inline-flex items-center justify-center w-8 h-8 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-all"
                  title="Ver ficha del cliente"
                >
                  <i class="fa fa-user" />
                </Link>
                <!-- Copiar enlace del portal público -->
                <button
                  class="inline-flex items-center justify-center w-8 h-8 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                  title="Copiar enlace portal de pagos"
                  @click="copyPortal(client)"
                >
                  <i class="fa fa-share-alt" />
                </button>
                <!-- WhatsApp si tiene teléfono -->
                <a
                  v-if="client.phone" :href="getWhatsAppLink(client)" target="_blank"
                  class="inline-flex items-center justify-center w-8 h-8 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all"
                  title="Enviar enlace por WhatsApp"
                >
                  <i class="fab fa-whatsapp" />
                </a>
                <!-- PDF estado de cuenta -->
                <a
                  :href="`/cobranzas/${client.id}/pdf`" target="_blank"
                  class="inline-flex items-center justify-center w-8 h-8 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                  title="Estado de cuenta PDF"
                >
                  <i class="fa fa-file-pdf" />
                </a>
              </div>
            </td>
          </tr>
          <tr v-if="clients.data.length === 0">
            <td colspan="6" class="py-20 text-center text-gray-400 italic">
              <i class="fa fa-check-circle text-3xl text-green-300 mb-3 block" />
              No hay clientes con saldo pendiente
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Mobile Cards -->
    <div class="grid grid-cols-1 gap-4 md:hidden">
      <div
        v-for="client in clients.data" :key="client.id"
        class="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex flex-col gap-3"
      >
        <div class="flex justify-between items-start">
          <div>
            <div class="font-semibold text-gray-900">{{ client.name }}</div>
            <div class="text-xs text-gray-400">{{ client.identification }}</div>
          </div>
          <span
            :class="getDaysClass(client.days_overdue)"
            class="px-2 py-1 rounded-full text-xs font-bold"
          >
            {{ client.days_overdue }}d
          </span>
        </div>
        <div class="flex items-center justify-between gap-2">
          <span class="text-xl font-bold text-red-600">{{ formatCurrency(client.balance) }}</span>
          <div class="flex items-center gap-2">
            <Link :href="`/clients/${client.id}`" class="text-xs font-medium text-teal-600 flex items-center gap-1">
              Ver ficha <i class="fa fa-chevron-right text-[10px]" />
            </Link>
            <button class="text-xs font-medium text-blue-500 flex items-center gap-1" @click="copyPortal(client)">
              <i class="fa fa-share-alt text-[10px]" /> Portal
            </button>
          </div>
        </div>
      </div>
      <div v-if="clients.data.length === 0" class="py-10 text-center text-gray-400 italic bg-white rounded-2xl border border-gray-100">
        No hay clientes con saldo pendiente
      </div>
    </div>

    <pagination v-if="clients.meta?.links" :links="clients.meta.links" class="mt-6" />

    <!-- Toast notification -->
    <transition name="fade">
      <div
        v-if="toastMessage"
        class="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 bg-gray-900 text-white text-sm rounded-xl shadow-xl flex items-center gap-2"
      >
        <i class="fa fa-check-circle text-green-400" />
        {{ toastMessage }}
      </div>
    </transition>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination'
import throttle from 'lodash/throttle'
import pickBy from 'lodash/pickBy'

export default {
  name: 'CobranzasIndex',
  components: { Head, Link, Pagination },
  layout: Layout,

  props: {
    clients: Object,
    stats: Object,
    filters: Object,
    account: Object,
  },

  data() {
    return {
      form: {
        search: this.filters?.search || '',
      },
      toastMessage: '',
    }
  },

  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get('/cobranzas', pickBy(this.form), { preserveState: true })
      }, 300),
    },
  },

  methods: {
    formatCurrency(value) {
      return new Intl.NumberFormat('es-VE', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      }).format(value || 0)
    },

    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('es-VE', {
        day: '2-digit', month: 'short', year: 'numeric',
      })
    },

    getDaysClass(days) {
      if (days > 60) return 'bg-red-100 text-red-700'
      if (days > 30) return 'bg-orange-100 text-orange-700'
      return 'bg-green-100 text-green-700'
    },

    copyPortal(client) {
      const url = `${window.location.origin}/public/client/${client.identification}`
      navigator.clipboard.writeText(url).then(() => {
        this.toastMessage = `✓ Enlace copiado para ${client.name}`
        setTimeout(() => { this.toastMessage = '' }, 3000)
      })
    },

    getWhatsAppLink(client) {
      const phone = client.phone.replace(/\D/g, '')
      const url = `${window.location.origin}/public/client/${client.identification}`
      const text = encodeURIComponent(`Hola ${client.name}, puedes consultar tu estado de cuenta y registrar tus pagos aquí: ${url}`)
      return `https://wa.me/${phone}?text=${text}`
    },
  },
}
</script>
